import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import api from "../../services/api";
import { formatDate, matchesQuery, moneyFx } from "./helpers";
import { EmptyHint, LoadingHint, tNegocio } from "./shared";
import { useStyles } from "./styles";

const t = (key, vars) => {
  let text = tNegocio(`pages.services.watoolx.${key}`);
  if (vars) Object.entries(vars).forEach(([k, v]) => { text = text.split(`{${k}}`).join(String(v)); });
  return text;
};
const TONE = { active: "#2e7d32", due_soon: "#c2410c", expired: "#c62828" };
const ORDER = { due_soon: 0, expired: 1, active: 2 };

/** Empresas WaToolX con plan pagado (vigente, por vencer o vencida hace ≤30 días). Solo lectura: no son NegocioSubscriptions. */
export const watoolxServiceRows = (companies) =>
  (companies || [])
    .filter((c) => c.planValue > 0 && (c.state === "active" || c.state === "due_soon" || (c.state === "expired" && c.daysLeft != null && c.daysLeft >= -30)))
    .sort((a, b) => (ORDER[a.state] - ORDER[b.state]) || ((a.daysLeft ?? 0) - (b.daysLeft ?? 0)));

export function useWatoolxServices(enabled) {
  const [state, setState] = useState({ loading: !!enabled, rows: [], error: false });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    api.get("/platform/clients")
      .then(({ data }) => { if (alive) setState({ loading: false, rows: watoolxServiceRows(data?.companies), error: false }); })
      .catch(() => { if (alive) setState({ loading: false, rows: [], error: true }); });
    return () => { alive = false; };
  }, [enabled]);
  return state;
}

export default function WatoolxServicesView({ data, query }) {
  const classes = useStyles();
  const history = useHistory();
  const rows = useMemo(
    () => data.rows.filter((r) => matchesQuery(r, query, [r.name, r.planName, r.phone, r.email, r.contact?.name, r.contact?.number, r.contact?.telegram, `#${r.id}`])),
    [data.rows, query]
  );
  const stateLabel = (r) => tNegocio(`pages.watoolx.stateOne.${r.state}`);
  const dueText = (r) => (r.daysLeft == null ? "" : r.daysLeft >= 0 ? t("left", { n: r.daysLeft }) : t("ago", { n: -r.daysLeft }));
  return (
    <>
      <div className={classes.kpiHint} style={{ margin: "8px 0" }}>
        {t("hint")}{" "}
        <Button size="small" color="primary" onClick={() => history.push("/negocio/watoolx")}>{t("manage")}</Button>
      </div>
      {data.loading ? (
        <LoadingHint />
      ) : data.error ? (
        <EmptyHint title={t("error")} />
      ) : !rows.length ? (
        <EmptyHint title={t("empty")} />
      ) : (
        <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t("company")}</TableCell>
                <TableCell>{t("contact")}</TableCell>
                <TableCell>{t("plan")}</TableCell>
                <TableCell>{t("monthly")}</TableCell>
                <TableCell>{t("due")}</TableCell>
                <TableCell>{t("state")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover style={{ cursor: "pointer" }} onClick={() => history.push("/negocio/watoolx")}>
                  <TableCell>
                    <div>{r.name}</div>
                    <div className={classes.captionBlock}>#{r.id}</div>
                  </TableCell>
                  <TableCell>
                    {r.contact ? (
                      <>
                        <div>{r.contact.name}</div>
                        <div className={classes.captionBlock}>{[r.contact.number, r.contact.telegram ? `@${r.contact.telegram}` : ""].filter(Boolean).join(" · ")}</div>
                      </>
                    ) : (
                      <span className={classes.captionBlock}>{t("noContact")}</span>
                    )}
                  </TableCell>
                  <TableCell>{r.planName || "—"}</TableCell>
                  <TableCell>{moneyFx(r.monthlyValue)}</TableCell>
                  <TableCell>
                    <div>{r.dueDay ? formatDate(r.dueDay) : "—"}</div>
                    <div className={classes.captionBlock}>{dueText(r)}</div>
                  </TableCell>
                  <TableCell style={{ color: TONE[r.state], fontWeight: 600 }}>{stateLabel(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
